export interface ProjectSummary {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface Project extends ProjectSummary {
  instructions: string;
  archived_at: string | null;
}

import { apiFetch } from "@/auth/token";
import { friendlyError } from "@/api/friendlyError";

async function jsonOk<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(friendlyError(res.status));
  return res.json() as Promise<T>;
}

const jsonHeaders = { "Content-Type": "application/json" };

/** Cookie-session request init. Identity rides the HttpOnly `egpt_session`
 *  cookie via `apiFetch`; no Authorization header. */
function reqInit(extra?: RequestInit): RequestInit {
  return {
    credentials: "include",
    ...extra,
    headers: {
      ...(extra?.headers ?? {}),
    },
  };
}

export async function listProjects(): Promise<ProjectSummary[]> {
  const r = await apiFetch(`/core/v1/projects`, reqInit());
  const body = await jsonOk<{ projects: ProjectSummary[] }>(r);
  return body.projects;
}

export async function getProject(id: string): Promise<Project> {
  const r = await apiFetch(`/core/v1/projects/${id}`, reqInit());
  return jsonOk<Project>(r);
}

export async function createProject(
  body: { name: string; instructions?: string },
): Promise<Project> {
  const r = await apiFetch(`/core/v1/projects`, reqInit({
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  }));
  return jsonOk<Project>(r);
}

export async function updateProject(
  id: string,
  body: { name?: string; instructions?: string },
): Promise<Project> {
  const r = await apiFetch(`/core/v1/projects/${id}`, reqInit({
    method: "PATCH",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  }));
  return jsonOk<Project>(r);
}

export async function archiveProject(id: string): Promise<void> {
  const r = await apiFetch(`/core/v1/projects/${id}`, reqInit({ method: "DELETE" }));
  if (!r.ok) throw new Error(friendlyError(r.status));
}

export async function unarchiveProject(id: string): Promise<void> {
  const r = await apiFetch(`/core/v1/projects/${id}/unarchive`, reqInit({ method: "POST" }));
  if (!r.ok) throw new Error(friendlyError(r.status));
}

export async function moveSession(sessionId: string, projectId: string | null): Promise<void> {
  const r = await apiFetch(`/core/v1/sessions/${sessionId}`, reqInit({
    method: "PATCH",
    headers: jsonHeaders,
    body: JSON.stringify({ project_id: projectId }),
  }));
  if (!r.ok) throw new Error(friendlyError(r.status));
}

export interface ProjectSession {
  id: string;
  title: string;
  updated_at: string;
}

/** Lists the chat sessions tagged with a project. The sessions list endpoint
 *  returns every session with its `project_id`; we filter to this project so
 *  the project page can show its conversations without a dedicated endpoint. */
export async function listSessionsForProject(projectId: string): Promise<ProjectSession[]> {
  const r = await apiFetch(`/core/v1/sessions`, reqInit());
  const body = await jsonOk<{
    sessions: Array<{
      session_id: string;
      title: string;
      created_at: string;
      last_active?: string;
      project_id?: string | null;
    }>;
  }>(r);
  return (body.sessions ?? [])
    .filter((s) => (s.project_id ?? null) === projectId)
    .map((s) => ({
      id: s.session_id,
      title: (s.title ?? "").trim() || "Untitled chat",
      updated_at: s.last_active ?? s.created_at,
    }));
}
